import { apiClient } from "@/lib/api-client";

/**
 * GST rates by HSN/SAC prefix (#514): `/api/v1/admin/catalog/gst-rates`.
 *
 * Rates go over the wire as basis points (1800 = 18%) and come back that way;
 * the forms send a percentage (`ratePercent`) and the server converts it.
 */

export type GstRateState = "in_force" | "scheduled" | "closed" | "withdrawn";

export interface GstRate {
	id: string;
	hsnPrefix: string;
	rateBp: number;
	effectiveFrom: string;
	effectiveTo: string | null;
	state: GstRateState;
	source: string;
	notificationSerial: string | null;
	origin: "seed" | "admin";
	signedOffBy: string | null;
	signedOffOn: string | null;
	signedOffRecordedBy: string | null;
	evidenceUrl: string | null;
	note: string | null;
	enteredBy: string | null;
	withdrawnAt: string | null;
	withdrawnReason: string | null;
	withdrawnBy: string | null;
	createdAt: string;
}

export interface GstRatePage {
	items: GstRate[];
	total: number;
	page: number;
	pageSize: number;
}

export interface GstPrefixChain {
	hsnPrefix: string;
	rows: GstRate[];
	latestRateId: string | null;
}

export interface GstPreviewSample {
	productId: string;
	productName: string;
	variantSku: string | null;
	city: string;
	rateBpBefore: number | null;
	rateBpAfter: number | null;
	amountBefore: number | null;
	amountAfter: number | null;
}

export interface GstRatePreview {
	hsnPrefix: string;
	latestRateId: string | null;
	productsInScope: number;
	productsRepriced: number;
	priceRowsRepriced: number;
	productsGainingPrice: number;
	productsLosingPrice: number;
	scheduled: boolean;
	startsAt: string;
	samples: GstPreviewSample[];
	sampleTotal: number;
}

export interface GstRepriceResult {
	repriced: number;
	failed: number;
	remaining: number;
	timedOut: boolean;
	/** The re-price stopped on an error after the change was saved. */
	interrupted: boolean;
}

export interface SignOffInput {
	signedOffBy: string;
	signedOffOn: string;
	notificationSerial: string;
	evidenceUrl?: string | null;
}

export interface AddGstRateInput extends SignOffInput {
	hsnPrefix: string;
	ratePercent: number;
	effectiveFrom: string;
	source: string;
	note?: string | null;
	expectedLatestRateId: string | null;
}

export interface CorrectGstRateInput extends SignOffInput {
	ratePercent: number;
	source: string;
	note?: string | null;
	reason: string;
	expectedLatestRateId: string | null;
}

export type GstPreviewRequest =
	| {
			kind: "add";
			hsnPrefix: string;
			ratePercent: number;
			effectiveFrom: string;
	  }
	| { kind: "cancel"; id: string }
	| { kind: "correct"; id: string; ratePercent: number };

const BASE = "/api/v1/admin/catalog/gst-rates";

export async function listGstRates(filters: {
	prefix?: string;
	states?: GstRateState[];
	page?: number;
}): Promise<GstRatePage> {
	const params = new URLSearchParams();
	if (filters.prefix) params.set("prefix", filters.prefix);
	for (const state of filters.states ?? []) params.append("state", state);
	if (filters.page) params.set("page", String(filters.page));
	const response = await apiClient.get(`${BASE}?${params.toString()}`);
	return response.data.data as GstRatePage;
}

export async function getGstPrefixChain(
	prefix: string,
): Promise<GstPrefixChain> {
	const response = await apiClient.get(
		`${BASE}/prefix/${encodeURIComponent(prefix)}`,
	);
	return response.data.data as GstPrefixChain;
}

export async function previewGstRateChange(
	request: GstPreviewRequest,
): Promise<GstRatePreview> {
	const response = await apiClient.post(`${BASE}/preview`, request);
	return response.data.data as GstRatePreview;
}

export async function addGstRate(
	input: AddGstRateInput,
): Promise<{ rate: GstRate; reprojection: GstRepriceResult }> {
	const response = await apiClient.post(BASE, input);
	return response.data.data;
}

export async function cancelGstRate(
	id: string,
	expectedLatestRateId: string | null,
): Promise<{ reopened: GstRate | null; reprojection: GstRepriceResult }> {
	const response = await apiClient.post(
		`${BASE}/${encodeURIComponent(id)}/cancel`,
		{ expectedLatestRateId },
	);
	return response.data.data;
}

export async function correctGstRate(
	id: string,
	input: CorrectGstRateInput,
): Promise<{ rate: GstRate; reprojection: GstRepriceResult }> {
	const response = await apiClient.post(
		`${BASE}/${encodeURIComponent(id)}/correct`,
		input,
	);
	return response.data.data;
}

export async function recordGstSignOff(
	id: string,
	input: SignOffInput,
): Promise<GstRate> {
	const response = await apiClient.post(
		`${BASE}/${encodeURIComponent(id)}/sign-off`,
		input,
	);
	return response.data.data as GstRate;
}

export async function repriceGstPrefix(
	hsnPrefix: string,
): Promise<GstRepriceResult> {
	const response = await apiClient.post(`${BASE}/reproject`, { hsnPrefix });
	return response.data.data as GstRepriceResult;
}

/** The server's field errors, keyed by field name without the `body.` prefix. */
export function fieldErrorsOf(error: unknown): Record<string, string> {
	const errors = (
		error as { response?: { data?: { errors?: Record<string, string> } } }
	)?.response?.data?.errors;
	const out: Record<string, string> = {};
	for (const [path, message] of Object.entries(errors ?? {})) {
		out[path.replace(/^(body|params|query)\./, "")] = message;
	}
	return out;
}

/** True when the server refused a save because the preview was stale (409). */
export function isStalePreview(error: unknown): boolean {
	return (
		(error as { response?: { status?: number } })?.response?.status === 409
	);
}
